// File: src/pages/admin/ReturnProcessManagement.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, IconButton, Typography, useMediaQuery, useTheme, Drawer,
  CircularProgress, Alert, Snackbar, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Chip, Grid, Card, TextField, Tabs, Tab, Pagination, InputAdornment,
  Divider, Checkbox, ListItemText, OutlinedInput, Collapse, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Paper,
} from '@mui/material';
import {
  Refresh, Visibility, Search, QrCode2, Download, Print, Close,
  KeyboardArrowDown, KeyboardArrowUp, Receipt, CheckCircle, Error as ErrorIcon,
  Warning, AssignmentReturn, LocalShipping, MonetizationOn, AllInbox,
  Email, CreditCard, Settings as SettingsIcon, Save, Info, ReceiptLong,
  ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useGetReturnItemsQuery,
  useApproveReturnMutation,
  useListOrdersQuery,
  ReturnItem,
  ORDER_STATUS_OPTIONS,
  getStatusText,
} from '../../features/admin/OrderlistApi.ts';

// Import return settings API
import {
  useGetProductReturnLimitQuery,
  useSetProductReturnLimitMutation,
} from '../../features/admin/globalSettingsApi.ts';

// Extracts a readable string from an RTK Query / fetch error object.
// Without this, rendering the raw error object in JSX prints "[object Object]".
const getErrorMessage = (err: unknown): string => {
  if (!err) return 'Something went wrong. Please try again.';
  if (typeof err === 'string') return err;

  const anyErr = err as any;

  // RTK Query FetchBaseQueryError shapes: { status, data } or { status: 'FETCH_ERROR', error }
  const data = anyErr?.data;
  if (data) {
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error && typeof data.error === 'string') return data.error;
  }

  if (anyErr?.status === 401 || anyErr?.status === 403) {
    return 'Please sign in to continue.';
  }

  if (typeof anyErr?.error === 'string') return anyErr.error;
  if (typeof anyErr?.message === 'string') return anyErr.message;

  return 'Failed to load data. Please try again.';
};

// ---------- Constants ----------
const RETURN_PROCESS_STATUSES = [14, 15, 18, 19, 21, 22, 23,24,25];
const ADMIN_RETURN_UPDATEABLE_STATUSES = [15, 19, 21, 22, 23,24];
const COMPLETED_STATUSES = [21, 22];

// Auto-update target statuses when QR/Invoice is generated
// Refund type  → 14 (Return refund in progress)
// Replacement type → 18 (Return Replacement in Progress)
const REFUND_AUTO_STATUS = 14;
const REPLACEMENT_AUTO_STATUS = 18;

// Statuses that are already "in progress" or further — skip auto-update if already here
const SKIP_AUTO_UPDATE_STATUSES = [14, 15, 18, 19, 21, 22, 23, 25, 10, 11];

const RAW_QR_ENDPOINT = process.env.REACT_APP_DELIVERY_ENDPOINT || '';
const QR_BASE_URL = RAW_QR_ENDPOINT.endsWith('/')
  ? RAW_QR_ENDPOINT.slice(0, -1)
  : RAW_QR_ENDPOINT;

const RAW_ENDPOINT = process.env.REACT_APP_SERVER_ENDPOINT || '';
const SERVER_ENDPOINT = RAW_ENDPOINT.endsWith('/') ? RAW_ENDPOINT : `${RAW_ENDPOINT}/`;

// Refund-only quick actions
const REFUND_ACTION_STATUSES = [
  { value: 24, label: 'Refund initiate',  icon: <Receipt fontSize="small" />,        color: '#3f51b5', tooltip: 'Mark as Refund initiate' },
  { value: 21, label: 'Refund Completed',  icon: <MonetizationOn fontSize="small" />, color: '#1A936F', tooltip: 'Mark as Refund Completed' },
];

// Replacement-only quick actions
const REPLACEMENT_ACTION_STATUSES = [
  { value: 22, label: 'Replacement Completed', icon: <CheckCircle fontSize="small" />,   color: '#114B5F', tooltip: 'Mark as Replacement Completed' },
  { value: 23, label: 'Ready for Pickup',       icon: <AllInbox fontSize="small" />,      color: '#FFA500', tooltip: 'Mark as Ready for Pickup' },
];

const REQUEST_TYPE_OPTIONS = [
  { value: 'Refund',      label: 'Refund' },
  { value: 'replacement', label: 'Replacement' },
];
const REFUND_MODE_OPTIONS = [
  { value: 'Wallet',        label: 'Wallet' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cash',          label: 'Cash' },
];

// ---------- Helper functions ----------
const formatDate = (dateString: string) =>
  dateString ? new Date(dateString).toLocaleDateString() : '-';
const formatDateTime = (dateString: string) =>
  dateString ? new Date(dateString).toLocaleString() : '-';
const formatCurrency = (amount: string | number) => {
  const n = Number(amount || 0);
  return `AED ${isNaN(n) ? amount : n.toFixed(2)}`;
};


// Build image URLs returned by the API. Product images are served from /images,
// while return/damage photos uploaded through returnOrder are served from /uploads.
// The API may return a filename, a relative path, or an absolute URL.
const getReturnImageUrl = (imagePath: unknown, type: 'product' | 'damage' = 'product'): string => {
  if (!imagePath) return '';
  const value = String(imagePath).trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;

  let cleanPath = value.replace(/^\/+/, '');
  cleanPath = cleanPath.replace(/^\.\//, '');

  // Backend serves both product and return uploads through /uploads.
  if (cleanPath.startsWith('uploads/')) return `${SERVER_ENDPOINT}${cleanPath}`;
  if (cleanPath.startsWith('uploads/images/')) return `${SERVER_ENDPOINT}${cleanPath}`;
  if (cleanPath.startsWith('images/')) return `${SERVER_ENDPOINT}${cleanPath}`;

  // Product catalog images are served from /images/<filename>.
  // Return/damage uploads are served from /uploads/<filename>.
  return type === 'damage'
    ? `${SERVER_ENDPOINT}uploads/${cleanPath}`
    : `${SERVER_ENDPOINT}images/${cleanPath}`;
};

// ---------- API Helper Functions ----------

const generateReturnQR = async (
  orderId: number,
  orderType: 'regular' | 'guest'
): Promise<{ pickup_code: string; qr_code: string; success: boolean }> => {
  
  const primaryUrl = `${QR_BASE_URL}/qrgenerater/${orderType}/${orderId}/qr`;
  const fallbackUrl = `${QR_BASE_URL}/qrgenerate/${orderType}/${orderId}/qr`;

  let response = await fetch(primaryUrl, { method: 'GET' });

  if (!response.ok) {
    response = await fetch(fallbackUrl, { method: 'GET' });
  }

  if (!response.ok) {
    throw new Error(`QR generation failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return {
    success: data.success || false,
    pickup_code: data.pickup_code || data.pickupCode || '',
    qr_code: data.qr_code || data.qrCode || data.qr || '',
  };
};

const fetchQRImage = async (
  orderId: number,
  orderType: 'regular' | 'guest'
): Promise<Blob | null> => {

  const primaryUrl = `${QR_BASE_URL}/qrgenerater/${orderType}/${orderId}/qr/image`;
  const fallbackUrl = `${QR_BASE_URL}/qrgenerate/${orderType}/${orderId}/qr/image`;

  try {
    let response = await fetch(primaryUrl, {
      method: 'GET',
      headers: { Accept: 'image/png' },
    });

    if (!response.ok) {
      response = await fetch(fallbackUrl, {
        method: 'GET',
        headers: { Accept: 'image/png' },
      });
    }

    if (!response.ok) return null;

    const blob = await response.blob();
    return blob.size > 0 ? blob : null;
  } catch (err) {
    console.warn('fetchQRImage failed:', err);
    return null;
  }
};

const generateReturnInvoice = async (
  orderId: number,
  returnId: number,
  userId: number | null | undefined,
  guestUserId: number | null | undefined
): Promise<{ error: boolean; message: string; dsr_no: string; download_url: string }> => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');

  const formData = new URLSearchParams();
  formData.append('order_id', orderId.toString());
  formData.append('return_id', returnId.toString());
  
  if (userId != null) formData.append('user_id', userId.toString());
  if (guestUserId != null) formData.append('guest_user_id', guestUserId.toString());

  const invoiceUrl = `${SERVER_ENDPOINT}user/generateReturnInvoice`;

  const response = await fetch(invoiceUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
    },
    body: formData,
  });

  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // body wasn't JSON
  }

  if (!response.ok) {
    throw new Error(data?.message || `Return invoice generation failed: ${response.status} ${response.statusText}`);
  }

  return {
    error: data.error || false,
    message: data.message || '',
    dsr_no: data.dsr_no || '',
    download_url: data.download_url || '',
  };
};

const generateOrderInvoice = async (
  orderId: number,
  userId: number | null | undefined
): Promise<{ error: boolean; message: string; invoice_no: string; download_url: string }> => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');

  const formData = new URLSearchParams();
  formData.append('order_id', orderId.toString());
  if (userId != null) formData.append('user_id', userId.toString());

  const invoiceUrl = `${SERVER_ENDPOINT}user/generateInvoice`;

  const response = await fetch(invoiceUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      authkey: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
    },
    body: formData,
  });

  let data: any = {};
  try {
    data = await response.json();
  } catch {
    // body wasn't JSON
  }

  if (!response.ok) {
    throw new Error(data?.message || `Invoice generation failed: ${response.status} ${response.statusText}`);
  }

  return {
    error: data.error || false,
    message: data.message || '',
    invoice_no: data.invoice_no || '',
    download_url: data.download_url || '',
  };
};

// ---------- QuickStatusActions Component ----------
const QuickStatusActions: React.FC<{
  item: ReturnItem;
  onUpdate: (item: ReturnItem, newStatus: number) => void;
  loadingId: string | null;
}> = ({ item, onUpdate, loadingId }) => {
  const actionStatuses = item.request_type?.toLowerCase() === 'refund'
    ? REFUND_ACTION_STATUSES
    : REPLACEMENT_ACTION_STATUSES;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {actionStatuses.map((action) => {
        const isCurrent = item.status === action.value;
        const isLoading = loadingId === `${item.return_id}_${action.value}`;

        return (
          <Tooltip key={action.value} title={action.label} arrow placement="top">
            <span>
              <IconButton
                size="small"
                aria-label={action.label}
                onClick={() => onUpdate(item, action.value)}
                disabled={isLoading}
                sx={{
                  width: 30,
                  minWidth: 30,
                  height: 30,
                  minHeight: 30,
                  p: 0,
                  borderRadius: '6px',
                  border: `1.5px solid ${action.color}`,
                  boxSizing: 'border-box',
                  color: isCurrent ? '#fff' : action.color,
                  backgroundColor: isCurrent ? action.color : 'transparent',
                  '&:hover': {
                    backgroundColor: action.color,
                    color: '#fff',
                    borderColor: action.color,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.65,
                    color: action.color,
                    borderColor: action.color,
                  },
                }}
              >
                {isLoading ? <CircularProgress size={13} /> : React.cloneElement(action.icon, { fontSize: 'small' })}
              </IconButton>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ---------- Main Component ----------
const ReturnProcessManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(() => {
    const saved = Number(localStorage.getItem('return_resolution_tab'));
    return Number.isInteger(saved) && saved >= 0 && saved <= 1 ? saved : 0;
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<ReturnItem | null>(null);
  const [imagePreview, setImagePreview] = useState<{ url: string; title: string } | null>(null);
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [resolutionChoice, setResolutionChoice] = useState<'replacement' | 'wallet' | 'refund' | ''>('');
  const [resolutionLoading, setResolutionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [requestTypeFilter, setRequestTypeFilter] = useState<string[]>([]);
  const [refundModeFilter, setRefundModeFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [quickActionLoadingId, setQuickActionLoadingId] = useState<string | null>(null);
  const [localStatusOverrides, setLocalStatusOverrides] = useState<Record<number, number>>({});

  // Return Settings states
  const [returnLimit, setReturnLimit] = useState('');
  const [hasReturnLimitChanges, setHasReturnLimitChanges] = useState(false);

  // QR Dialog state
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrLoadingId, setQrLoadingId] = useState<number | null>(null);
  const [generatedQR, setGeneratedQR] = useState<{ pickupCode: string; qrCode: string } | null>(null);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrForReturn, setQrForReturn] = useState<ReturnItem | null>(null);

  // Invoice states
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<number | null>(null);
  const [returnInvoiceLoadingId, setReturnInvoiceLoadingId] = useState<number | null>(null);

  // ── Scroll arrow + top scrollbar — callback-ref approach ──
  // Using callback refs so listeners attach the moment the DOM nodes appear
  // (avoids the "conditional render / useEffect timing" bug).
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const topScrollRef   = useRef<HTMLDivElement | null>(null);
  const topScrollInnerRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScroll   = useRef(false);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Called every time scroll position or size may have changed
  const syncScrollState = React.useCallback(() => {
    const table = tableScrollRef.current;
    const inner = topScrollInnerRef.current;
    if (!table) return;
    setCanScrollLeft(table.scrollLeft > 0);
    setCanScrollRight(table.scrollLeft + table.clientWidth < table.scrollWidth - 1);
    if (inner) inner.style.width = `${table.scrollWidth}px`;
  }, []);

  // Callback ref for the TABLE scroll container
  const setTableRef = React.useCallback((node: HTMLDivElement | null) => {
    if (tableScrollRef.current) {
      tableScrollRef.current.removeEventListener('scroll', syncScrollState);
    }
    tableScrollRef.current = node;
    if (!node) return;

    const onTableScroll = () => {
      syncScrollState();
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      if (topScrollRef.current) topScrollRef.current.scrollLeft = node.scrollLeft;
      isSyncingScroll.current = false;
    };
    node.addEventListener('scroll', onTableScroll);
    window.addEventListener('resize', syncScrollState);
    // Initial measurement (defer one tick so table has rendered its full width)
    setTimeout(syncScrollState, 0);
  }, [syncScrollState]);

  // Callback ref for the TOP scrollbar track
  const setTopRef = React.useCallback((node: HTMLDivElement | null) => {
    if (topScrollRef.current) {
      topScrollRef.current.removeEventListener('scroll', () => {});
    }
    topScrollRef.current = node;
    if (!node) return;

    const onTopScroll = () => {
      if (isSyncingScroll.current) return;
      isSyncingScroll.current = true;
      if (tableScrollRef.current) {
        tableScrollRef.current.scrollLeft = node.scrollLeft;
        syncScrollState();
      }
      isSyncingScroll.current = false;
    };
    node.addEventListener('scroll', onTopScroll);
  }, [syncScrollState]);

  // Callback ref for the phantom inner div (just store it)
  const setTopInnerRef = React.useCallback((node: HTMLDivElement | null) => {
    topScrollInnerRef.current = node;
  }, []);

  // Re-measure when data / filters / page change
  useEffect(() => {
    setTimeout(syncScrollState, 50);
  }, [tabValue, page, searchStr, statusFilter, requestTypeFilter, refundModeFilter, syncScrollState]);

  const handleScrollLeft  = () => tableScrollRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const handleScrollRight = () => tableScrollRef.current?.scrollBy({ left:  300, behavior: 'smooth' });

  // ---------- RTK Query Hooks ----------
  const { data, isLoading, error, refetch } = useGetReturnItemsQuery(undefined, {
    pollingInterval: 10000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [approveReturn] = useApproveReturnMutation();

  const { refetch: refetchOrders } = useListOrdersQuery(undefined, {
    pollingInterval: 10000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Return Settings hooks
  const { 
    data: limitData, 
    isLoading: isLimitLoading, 
    error: limitError, 
    isError: isLimitError, 
    refetch: refetchLimit 
  } = useGetProductReturnLimitQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [setProductReturnLimit, { isLoading: isLimitSaving }] = useSetProductReturnLimitMutation();

  // Load initial return limit data
  useEffect(() => {
    console.log('=== limitData received ===', limitData);
    
    if (limitData && limitData.success) {
      if (limitData.return_limit !== undefined && limitData.return_limit !== null) {
        setReturnLimit(String(limitData.return_limit));
      } else if (limitData.global_return_limit !== undefined && limitData.global_return_limit !== null) {
        setReturnLimit(String(limitData.global_return_limit));
      } else {
        setReturnLimit('3');
      }
      setHasReturnLimitChanges(false);
    }
  }, [limitData]);

  // Update hasReturnLimitChanges when return limit changes
  useEffect(() => {
    if (limitData && limitData.success) {
      const originalValue = limitData.return_limit || limitData.global_return_limit || '3';
      setHasReturnLimitChanges(String(originalValue) !== returnLimit);
    }
  }, [returnLimit, limitData]);

  const rawReturnItems = useMemo(() => data?.data || [], [data]);

  // item.status here is return_items.status — a RETURN-flow code (10 Return
  // Requested, 11 Refund Requested, 14/15/18/19/21/22/23/24/25 …). It must
  // NEVER be replaced with the parent ORDER's status (orders.status — 0
  // Payment Pending, 1 Payment Success, 7 Delivered, …): those are two
  // separate code spaces that happen to reuse the same numbers, so blending
  // one into the other silently relabels a return row as "Delivered" /
  // "Payment Success" instead of showing its actual return progress.
  //
  // This used to be "kept in sync" with the live order status (see git
  // history), which is exactly why: a partial return on a multi-item order
  // intentionally leaves order.status untouched (the other item(s) keep
  // flowing through their own delivery lifecycle — see backend
  // /returnOrder's isFullReturn gate), so order.status and this return's own
  // status legitimately differ for the entire lifetime of an in-progress
  // return. Overwriting item.status with order.status here made every such
  // return permanently show the order's status instead of its own.
  const returnItems = useMemo(() =>
    rawReturnItems.map(item => {
      if (localStatusOverrides[item.return_id] !== undefined) {
        return { ...item, status: localStatusOverrides[item.return_id] };
      }
      return item;
    }),
    [rawReturnItems, localStatusOverrides]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        refetch();
        refetchOrders();
        refetchLimit();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch, refetchOrders, refetchLimit]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
        refetchOrders();
        refetchLimit();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetch, refetchOrders, refetchLimit]);

  // ---------- Auto-status update helper ----------
  const handleAutoUpdateReturnStatus = async (item: ReturnItem): Promise<void> => {
    // Back-office approval is mandatory before any processing/agent transition.
    if (!item.return_approved) {
      setSnackbar({
        open: true,
        message: `Return #${item.return_id} must be approved by back-office before agent dispatch.`,
        severity: 'error',
      });
      return;
    }
    if (SKIP_AUTO_UPDATE_STATUSES.includes(item.status)) return;

    const isRefund = item.request_type?.toLowerCase() === 'refund';
    const targetStatus = isRefund ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS;
    const targetLabel = getStatusText(targetStatus);

    setLocalStatusOverrides(prev => ({ ...prev, [item.return_id]: targetStatus }));

    try {
      await approveReturn({ return_id: item.return_id, type: targetStatus }).unwrap();

      setSnackbar({
        open: true,
        message: `Return #${item.return_id} status auto-updated to: ${targetLabel}`,
        severity: 'info',
      });

      await Promise.all([refetch(), refetchOrders()]);

      setLocalStatusOverrides(prev => {
        const next = { ...prev };
        delete next[item.return_id];
        return next;
      });

      setTimeout(() => { refetch(); refetchOrders(); }, 1500);
    } catch (err: any) {
      console.warn('Auto-status update failed (non-blocking):', err);
      setLocalStatusOverrides(prev => {
        const next = { ...prev };
        delete next[item.return_id];
        return next;
      });
    }
  };

  // Apply filters
  const filteredItems = useMemo(() => {
    let result = returnItems;

    if (searchStr) {
      const s = searchStr.toLowerCase();
      result = result.filter(
        (item) =>
          item.id.toString().includes(s) ||
          item.order_id.toString().includes(s) ||
          item.return_id.toString().includes(s) ||
          item.product_name?.toLowerCase().includes(s) ||
          item.reason.toLowerCase().includes(s) ||
          item.user_email?.toLowerCase().includes(s) ||
          item.transaction_id?.toLowerCase().includes(s)
      );
    }

    if (statusFilter.length) {
      result = result.filter((item) => statusFilter.includes(item.status));
    }
    if (requestTypeFilter.length) {
      result = result.filter((item) =>
        requestTypeFilter.some((r) => r.toLowerCase() === item.request_type.toLowerCase())
      );
    }
    if (refundModeFilter.length) {
      result = result.filter((item) => refundModeFilter.includes(item.refund_mode));
    }

    return result;
  }, [returnItems, searchStr, statusFilter, requestTypeFilter, refundModeFilter]);

  const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
  const paginatedItems = filteredItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // ---------- Handlers ----------

  // Return Limit Handlers
  const handleReturnLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || /^\d+$/.test(value)) setReturnLimit(value);
  };

  const handleResetReturnLimit = () => {
    if (limitData && limitData.success) {
      setReturnLimit(String(limitData.return_limit || limitData.global_return_limit || '3'));
      setHasReturnLimitChanges(false);
    }
  };

  const handleSaveReturnLimit = async () => {
    if (!returnLimit || parseInt(returnLimit) < 0) {
      setSnackbar({ open: true, message: 'Please enter a valid return limit', severity: 'error' });
      return;
    }

    try {
      const result = await setProductReturnLimit({ return_limit: returnLimit }).unwrap();
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Product return limit updated successfully!', severity: 'success' });
        setHasReturnLimitChanges(false);
        await refetchLimit();
      } else {
        throw new Error(result.message || 'Failed to update return limit');
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Error updating product return limit.', severity: 'error' });
    }
  };

  const handleApproveReturn = async (resolution: 'replacement' | 'wallet' | 'refund') => {
    if (!selectedReturn) return;
    setResolutionLoading(true);
    try {
      const response = await approveReturn({
        return_id: selectedReturn.return_id,
        action: 'approve',
        resolution: resolution === 'refund' ? 'original' : resolution,
      }).unwrap();

      const updated = {
        ...selectedReturn,
        return_approved: 1,
        approved_at: new Date().toISOString(),
        status: response.status,
        refund_mode: resolution === 'wallet' ? 'Wallet' : selectedReturn.refund_mode,
      } as ReturnItem;

      setSelectedReturn(updated);
      setLocalStatusOverrides(prev => ({ ...prev, [selectedReturn.return_id]: response.status }));
      setResolutionDialogOpen(false);
      setResolutionChoice('');
      setSnackbar({
        open: true,
        message: `Return #${selectedReturn.return_id} approved: ${resolution === 'replacement' ? 'Replacement' : resolution === 'wallet' ? 'Voto Credits' : 'Original Payment Refund'}`,
        severity: 'success',
      });
      await Promise.all([refetch(), refetchOrders()]);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.data?.message || err?.message || 'Return approval failed',
        severity: 'error',
      });
    } finally {
      setResolutionLoading(false);
    }
  };

  const handleQuickStatusUpdate = async (item: ReturnItem, newStatus: number) => {
    // Keep all action buttons visible/normal, but enforce the return approval workflow
    // in the handler so an unapproved return can never be dispatched/processed.
    if (!item.return_approved) {
      setSnackbar({
        open: true,
        message: `Return #${item.return_id} must be approved with a resolution before this action can be processed.`,
        severity: 'warning',
      });
      return;
    }

    if (COMPLETED_STATUSES.includes(item.status) || item.status === newStatus) {
      return;
    }

    setQuickActionLoadingId(`${item.return_id}_${newStatus}`);
    setLocalStatusOverrides(prev => ({ ...prev, [item.return_id]: newStatus }));

    try {
      const response = await approveReturn({ return_id: item.return_id, type: newStatus }).unwrap();

      setSnackbar({
        open: true,
        message: `Return #${item.return_id} updated to: ${response.status_text || newStatus}`,
        severity: 'success',
      });

      await Promise.all([refetch(), refetchOrders()]);

      setLocalStatusOverrides(prev => {
        const next = { ...prev };
        delete next[item.return_id];
        return next;
      });

      setTimeout(() => { refetch(); refetchOrders(); }, 1500);
    } catch (err: any) {
      setLocalStatusOverrides(prev => {
        const next = { ...prev };
        delete next[item.return_id];
        return next;
      });
      setSnackbar({ open: true, message: err?.data?.message || err?.message || 'Update failed', severity: 'error' });
    } finally {
      setQuickActionLoadingId(null);
    }
  };

  const handleGenerateQR = async (item: ReturnItem) => {
    setQrLoadingId(item.return_id);
    try {
      const orderType: 'regular' | 'guest' = item.order_type === 'guest' ? 'guest' : 'regular';

      await handleAutoUpdateReturnStatus(item);

      const qrData = await generateReturnQR(item.order_id, orderType);

      setGeneratedQR({ pickupCode: qrData.pickup_code, qrCode: qrData.qr_code });

      const blob = await fetchQRImage(item.order_id, orderType);
      if (blob) {
        setQrImageUrl(URL.createObjectURL(blob));
      } else if (qrData.qr_code) {
        setQrImageUrl(`data:image/png;base64,${qrData.qr_code}`);
      } else {
        setQrImageUrl(null);
      }

      setQrForReturn(item);
      setQrDialogOpen(true);

      setSnackbar({ open: true, message: 'QR code generated successfully!', severity: 'success' });
    } catch (err: any) {
      alert(err);
      console.error('QR generation error:', err);
      setSnackbar({
        open: true,
        message: err?.message || 'QR generation failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setQrLoadingId(null);
    }
  };

  const handleGenerateOrderInvoice = async (item: ReturnItem) => {
    setInvoiceLoadingId(item.return_id);
    try {
      await handleAutoUpdateReturnStatus(item);

      const userId = item.user_id ?? null;
      const result = await generateOrderInvoice(item.order_id, userId);

      if (!result.error && result.download_url) {
        window.open(result.download_url, '_blank');
        setSnackbar({
          open: true,
          message: `Order Invoice ${result.invoice_no} generated successfully!`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Order invoice generation failed',
          severity: 'error',
        });
      }
    } catch (err: any) {
      console.error('Order invoice generation error:', err);
      setSnackbar({
        open: true,
        message: err?.message || 'Order invoice generation failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  const handleGenerateReturnInvoice = async (item: ReturnItem) => {
    setReturnInvoiceLoadingId(item.return_id);
    try {
      await handleAutoUpdateReturnStatus(item);

      const userId = item.user_id ?? null;
      const guestUserId = item.guest_user_id ?? null;
      const result = await generateReturnInvoice(item.order_id, item.return_id, userId, guestUserId);

      if (!result.error && result.download_url) {
        window.open(result.download_url, '_blank');
        setSnackbar({
          open: true,
          message: `Return Invoice DSR-${result.dsr_no} generated successfully!`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Return invoice generation failed',
          severity: 'error',
        });
      }
    } catch (err: any) {
      console.error('Return invoice generation error:', err);
      setSnackbar({
        open: true,
        message: err?.message || 'Return invoice generation failed. Please try again.',
        severity: 'error',
      });
    } finally {
      setReturnInvoiceLoadingId(null);
    }
  };

  const handleCloseQrDialog = () => {
    if (qrImageUrl?.startsWith('blob:')) URL.revokeObjectURL(qrImageUrl);
    setQrDialogOpen(false);
    setGeneratedQR(null);
    setQrImageUrl(null);
    setQrForReturn(null);
  };

  const handleDownloadQR = () => {
    if (!qrImageUrl || !qrForReturn) return;
    const a = document.createElement('a');
    a.href = qrImageUrl;
    a.download = `return-qr-${qrForReturn.return_id}.png`;
    a.click();
  };

  const handlePrintQR = () => {
    if (!qrForReturn || !generatedQR) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html><head><title>Return QR #${qrForReturn.return_id}</title>
      <style>body{font-family:Arial;padding:24px;text-align:center}
      .pickup-code{font-family:monospace;font-size:22px;font-weight:700;letter-spacing:4px;padding:12px;background:#f5f5f5;border-radius:8px}
      </style></head>
      <body>
        <h2>Return #${qrForReturn.return_id}</h2>
        <p>Order #${qrForReturn.order_id}</p>
        <img src="${qrImageUrl}" style="max-width:200px;margin:16px auto;display:block"/>
        <div class="pickup-code">${generatedQR.pickupCode}</div>
        <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  const getReturnStatusChip = (item: ReturnItem) => {
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === item.status);
    return (
      <Chip
        label={getStatusText(item.status)}
        size="small"
        sx={{
          backgroundColor: statusOption?.color || '#9e9e9e',
          color: 'white',
          fontWeight: 600,
          fontSize: '11px',
        }}
      />
    );
  };

  const handleViewDetails = (item: ReturnItem) => {
    const overriddenItem = localStatusOverrides[item.return_id] !== undefined
      ? { ...item, status: localStatusOverrides[item.return_id] }
      : item;
    setSelectedReturn(overriddenItem);
    setDetailsDialogOpen(true);
  };

  const handleRefresh = async () => {
    try {
      await Promise.all([refetch(), refetchOrders(), refetchLimit()]);
      setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to refresh data', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleStatusFilterChange = (e: any) => setStatusFilter(e.target.value);
  const handleRequestTypeFilterChange = (e: any) => setRequestTypeFilter(e.target.value);
  const handleRefundModeFilterChange = (e: any) => setRefundModeFilter(e.target.value);
  const handleClearFilters = () => {
    setSearchStr('');
    setStatusFilter([]);
    setRequestTypeFilter([]);
    setRefundModeFilter([]);
  };

  const handleToggleRow = (id: number) =>
    setExpandedRows((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ---------- Render ----------
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(!mobileOpen)} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <AdminHeader onToggleMenu={() => setMobileOpen(!mobileOpen)} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={600}>Return Process Management</Typography>
                <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}>
                  Refresh
                </Button>
              </Box>

              {/* Main Card with Tabs */}
              <Card sx={{ borderRadius: 2, mb: 3 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); localStorage.setItem('return_resolution_tab', String(v)); }}>
                    <Tab label={`All Returns (${returnItems.length})`} />
                    <Tab label="Return Settings" icon={<SettingsIcon />} iconPosition="start" />
                  </Tabs>
                </Box>

                {/* Tab 0: Returns List */}
                {tabValue === 0 && (
                  <>
                    {/* Filters */}
                    <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                          size="small"
                          placeholder="Search returns..."
                          value={searchStr}
                          onChange={(e) => setSearchStr(e.target.value)}
                          sx={{ flex: 1, minWidth: 200 }}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
                        />

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Return Status</InputLabel>
                          <Select
                            multiple
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            input={<OutlinedInput label="Return Status" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((v) => {
                                  const opt = ORDER_STATUS_OPTIONS.find((o) => o.value === v);
                                  return <Chip key={v} label={opt?.label || v} size="small" sx={{ bgcolor: opt?.color, color: 'white' }} />;
                                })}
                              </Box>
                            )}
                          >
                            {ORDER_STATUS_OPTIONS.filter((o) => RETURN_PROCESS_STATUSES.includes(o.value)).map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                <Checkbox checked={statusFilter.includes(s.value)} />
                                <ListItemText primary={s.label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {!!(searchStr || statusFilter.length || requestTypeFilter.length || refundModeFilter.length) && (
                          <Button variant="outlined" onClick={handleClearFilters}>Clear Filters</Button>
                        )}
                      </Box>
                    </Box>

                    {isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                      </Box>
                    )}

                    {error && !isLoading && (
                      <Box sx={{ p: 3 }}>
                        <Alert severity="error" action={<Button color="inherit" onClick={handleRefresh}>Retry</Button>}>
                          {getErrorMessage(error)}
                        </Alert>
                      </Box>
                    )}

                    {!isLoading && !error && filteredItems.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>No return items found</Typography>
                        <Button variant="outlined" onClick={handleClearFilters}>Clear Filters</Button>
                      </Box>
                    )}

                    {!isLoading && !error && filteredItems.length > 0 && (
                      <>
                        {/* ── Desktop Table with Top Scrollbar + Arrows ── */}
                        <Box
                          sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}
                        >
                          {/* ── Top mirror scrollbar row ── */}
                          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                            <IconButton
                              onClick={handleScrollLeft}
                              size="small"
                              disabled={!canScrollLeft}
                              sx={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: 0,
                                bgcolor: '#f5f5f5', border: '1px solid #c0c0c0', borderRight: 'none',
                                color: canScrollLeft ? '#444' : '#bbb',
                                '&:hover:not(:disabled)': { bgcolor: '#e0e0e0' },
                              }}
                            >
                              <ChevronLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Box
                              ref={setTopRef}
                              sx={{
                                flex: 1, overflowX: 'scroll', overflowY: 'hidden', height: 14,
                                '&::-webkit-scrollbar': { height: 14 },
                                '&::-webkit-scrollbar-track': { bgcolor: '#f5f5f5' },
                                '&::-webkit-scrollbar-thumb': { bgcolor: '#aaa', borderRadius: 2, border: '2px solid #f5f5f5' },
                                '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#888' },
                              }}
                            >
                              <Box ref={setTopInnerRef} sx={{ height: 1, width: '100%' }} />
                            </Box>
                            <IconButton
                              onClick={handleScrollRight}
                              size="small"
                              disabled={!canScrollRight}
                              sx={{
                                flexShrink: 0, width: 28, height: 28, borderRadius: 0,
                                bgcolor: '#f5f5f5', border: '1px solid #c0c0c0', borderLeft: 'none',
                                color: canScrollRight ? '#444' : '#bbb',
                                '&:hover:not(:disabled)': { bgcolor: '#e0e0e0' },
                              }}
                            >
                              <ChevronRight sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>

                          {/* Scrollable table container */}
                          <Box
                            ref={setTableRef}
                            sx={{ width: '100%', overflowX: 'auto' }}
                          >
                            <Table sx={{ minWidth: 1262, tableLayout: 'fixed', width: '100%' }}>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                                  <TableCell sx={{ width: 62,  px: 1, whiteSpace: 'nowrap' }}>Return ID</TableCell>
                                  <TableCell sx={{ width: 62,  px: 1, whiteSpace: 'nowrap' }}>Order ID</TableCell>
                                  <TableCell sx={{ width: 190, px: 1 }}>Product</TableCell>
                                  <TableCell sx={{ width: 100, px: 1 }}>Type</TableCell>
                                  <TableCell sx={{ width: 180, px: 1 }}>Customer</TableCell>
                                  <TableCell sx={{ width: 130, px: 1 }}>Transaction ID</TableCell>
                                  <TableCell sx={{ width: 160, px: 1 }}>Return Status</TableCell>
                                  <TableCell sx={{ width: 88,  px: 1 }}>Amount</TableCell>
                                  <TableCell sx={{ width: 80,  px: 1 }}>Created</TableCell>
                                  <TableCell sx={{ width: 240, px: 1 }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paginatedItems.map((item) => (
                                  <TableRow key={item.id} hover>
                                    <TableCell sx={{ px: 1 }}>#{item.return_id}</TableCell>
                                    <TableCell sx={{ px: 1 }}>#{item.order_id}</TableCell>

                                    <TableCell sx={{ px: 1, overflow: 'hidden' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {item.product_image ? (
                                          <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                                            <img
                                              src={getReturnImageUrl(item.product_image, 'product')}
                                              alt={item.product_name || 'Product'}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                                            />
                                          </Box>
                                        ) : (
                                          <Box sx={{ width: 52, height: 52, flexShrink: 0, borderRadius: 1, border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'text.secondary' }}>No image</Box>
                                        )}
                                        <Box sx={{ minWidth: 0 }}>
                                          <Tooltip title={item.product_name || `Product ${item.product_id}`} arrow placement="top">
                                            <Typography variant="body2" noWrap>{item.product_name || `Product ${item.product_id}`}</Typography>
                                          </Tooltip>
                                          <Typography variant="caption" color="text.secondary">Qty: {item.return_qty}</Typography>
                                          {/* Vendor vs Voto (in-house) product — a vendor
                                              product's return is serviced by that vendor from
                                              their own portal, so it matters at a glance which
                                              kind of row this is. */}
                                          {item.is_vendor_product ? (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                                              Vendor: {item.vendor_name || `Vendor ${item.vendor_id}`}
                                            </Typography>
                                          ) : (
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              color="default"
                                              label="Voto product"
                                              sx={{ display: 'block', mt: 0.4, height: 18, fontSize: 10, width: 'fit-content' }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    </TableCell>

                                    <TableCell sx={{ px: 1 }}>
                                      <Chip
                                        label={item.request_type?.toLowerCase() === 'refund' ? 'Refund' : 'Replacement'}
                                        size="small"
                                        color={item.request_type?.toLowerCase() === 'refund' ? 'primary' : 'secondary'}
                                      />
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                        {item.refund_mode}
                                      </Typography>
                                    </TableCell>

                                    <TableCell sx={{ px: 1, overflow: 'hidden' }}>
                                      <Typography variant="body2" fontWeight={600} noWrap>
                                        {item.customer_name || item.guest_customer_name || '—'}
                                      </Typography>
                                      <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                        {item.customer_mobile || item.guest_customer_mobile || '—'}
                                      </Typography>
                                      <Typography variant="caption" display="block" sx={{ color: '#1565c0' }} noWrap>
                                        {item.user_email || item.guest_email || '—'}
                                      </Typography>
                                    </TableCell>

                                    <TableCell sx={{ px: 1, overflow: 'hidden' }}>
                                      <Tooltip title={item.transaction_id || '—'} arrow placement="top">
                                        <Typography
                                          variant="body2"
                                          noWrap
                                          sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                                        >
                                          {item.transaction_id || '—'}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>

                                    <TableCell sx={{ px: 1 }}>
                                      {getReturnStatusChip(item)}
                                    </TableCell>

                                    <TableCell sx={{ px: 1 }}>
                                      <Typography fontWeight={600} variant="body2">{formatCurrency(item.refund_amount)}</Typography>
                                    </TableCell>

                                    <TableCell sx={{ px: 1 }}>
                                      <Typography variant="body2">{formatDate(item.created_at)}</Typography>
                                    </TableCell>

                                    {/* ── Actions column ── */}
                                    <TableCell sx={{ px: 1 }}>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>

                                        {/* Row 1 – utility icons */}
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                          <Tooltip title="View Details" arrow placement="top">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleViewDetails(item)}
                                              sx={{
                                                width: 30, height: 30, borderRadius: '6px',
                                                border: '1.5px solid #1976d2', color: '#1976d2',
                                                '&:hover': { bgcolor: '#1976d2', color: '#fff' },
                                                transition: 'all 0.15s ease',
                                              }}
                                            >
                                              <Visibility sx={{ fontSize: 15 }} />
                                            </IconButton>
                                          </Tooltip>

                                          <Tooltip
                                            title={
                                              !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                                ? `Generate QR · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                                : 'Generate QR'
                                            }
                                            arrow
                                            placement="top"
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleGenerateQR(item)}
                                                disabled={qrLoadingId === item.return_id}
                                                sx={{
                                                  width: 30, height: 30, borderRadius: '6px',
                                                  border: `1.5px solid ${qrLoadingId === item.return_id ? '#d0d0d0' : '#0288d1'}`,
                                                  color: qrLoadingId === item.return_id ? '#d0d0d0' : '#0288d1',
                                                  '&:hover:not(:disabled)': { bgcolor: '#0288d1', color: '#fff' },
                                                  transition: 'all 0.15s ease',
                                                }}
                                              >
                                                {qrLoadingId === item.return_id
                                                  ? <CircularProgress size={13} sx={{ color: 'inherit' }} />
                                                  : <QrCode2 sx={{ fontSize: 15 }} />
                                                }
                                              </IconButton>
                                            </span>
                                          </Tooltip>

                                          <Tooltip
                                            title={
                                              !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                                ? `Generate Order Invoice · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                                : 'Generate Order Invoice'
                                            }
                                            arrow
                                            placement="top"
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleGenerateOrderInvoice(item)}
                                                disabled={invoiceLoadingId === item.return_id}
                                                sx={{
                                                  width: 30, height: 30, borderRadius: '6px',
                                                  border: `1.5px solid ${invoiceLoadingId === item.return_id ? '#d0d0d0' : '#f57c00'}`,
                                                  color: invoiceLoadingId === item.return_id ? '#d0d0d0' : '#f57c00',
                                                  '&:hover:not(:disabled)': { bgcolor: '#f57c00', color: '#fff' },
                                                  transition: 'all 0.15s ease',
                                                }}
                                              >
                                                {invoiceLoadingId === item.return_id
                                                  ? <CircularProgress size={13} sx={{ color: 'inherit' }} />
                                                  : <Receipt sx={{ fontSize: 15 }} />
                                                }
                                              </IconButton>
                                            </span>
                                          </Tooltip>

                                          <Tooltip
                                            title={
                                              !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                                ? `Generate Return Invoice · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                                : 'Generate Return Invoice'
                                            }
                                            arrow
                                            placement="top"
                                          >
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleGenerateReturnInvoice(item)}
                                                disabled={returnInvoiceLoadingId === item.return_id}
                                                sx={{
                                                  width: 30, height: 30, borderRadius: '6px',
                                                  border: `1.5px solid ${returnInvoiceLoadingId === item.return_id ? '#d0d0d0' : '#9c27b0'}`,
                                                  color: returnInvoiceLoadingId === item.return_id ? '#d0d0d0' : '#9c27b0',
                                                  '&:hover:not(:disabled)': { bgcolor: '#9c27b0', color: '#fff' },
                                                  transition: 'all 0.15s ease',
                                                }}
                                              >
                                                {returnInvoiceLoadingId === item.return_id
                                                  ? <CircularProgress size={13} sx={{ color: 'inherit' }} />
                                                  : <ReceiptLong sx={{ fontSize: 15 }} />
                                                }
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </Box>

                                        {/* Row 2 – quick status actions */}
                                        <QuickStatusActions
                                          item={item}
                                          onUpdate={handleQuickStatusUpdate}
                                          loadingId={quickActionLoadingId}
                                        />

                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Box>

                        {/* Mobile Cards */}
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                          {paginatedItems.map((item) => (
                            <Card key={item.id} sx={{ m: 2, p: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Box>
                                  <Typography variant="h6">Return #{item.return_id}</Typography>
                                  <Typography variant="body2" color="text.secondary">Order #{item.order_id} • {formatDate(item.created_at)}</Typography>
                                </Box>
                                <IconButton size="small" onClick={() => handleToggleRow(item.id)}>
                                  {expandedRows.includes(item.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>
                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                <Grid item xs={6}><Typography variant="caption">Product</Typography><Typography variant="body2">{item.product_name || `Product ${item.product_id}`}</Typography></Grid>
                                <Grid item xs={6}><Typography variant="caption">Qty</Typography><Typography variant="body2">{item.return_qty}</Typography></Grid>
                                <Grid item xs={6}>
                                  <Chip
                                    label={item.request_type?.toLowerCase() === 'refund' ? 'Refund' : 'Replacement'}
                                    size="small"
                                    color={item.request_type?.toLowerCase() === 'refund' ? 'primary' : 'secondary'}
                                  />
                                </Grid>
                                <Grid item xs={6}><Typography variant="caption">Amount</Typography><Typography fontWeight={600}>{formatCurrency(item.refund_amount)}</Typography></Grid>
                                <Grid item xs={12}>{getReturnStatusChip(item)}</Grid>
                              </Grid>
                              <Collapse in={expandedRows.includes(item.id)}>
                                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                  <Typography variant="caption" display="block">User: {item.user_email || '—'}</Typography>
                                  <Typography variant="caption" display="block">Transaction: {item.transaction_id || '—'}</Typography>
                                  <Typography variant="caption" display="block">Reason: {item.reason}</Typography>
                                </Box>
                              </Collapse>
                              <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="caption" display="block" sx={{ mb: 1 }}>Actions:</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <Tooltip title="View Details" arrow>
                                    <IconButton size="small" onClick={() => handleViewDetails(item)}
                                      sx={{ width: 30, height: 30, borderRadius: '6px', border: '1.5px solid #1976d2', color: '#1976d2' }}>
                                      <Visibility sx={{ fontSize: 15 }} />
                                    </IconButton>
                                  </Tooltip>
                                  
                                  <Tooltip
                                    title={
                                      !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                        ? `Generate QR · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                        : 'Generate QR'
                                    }
                                    arrow
                                  >
                                    <span>
                                      <IconButton size="small" onClick={() => handleGenerateQR(item)} disabled={qrLoadingId === item.return_id}
                                        sx={{ width: 30, height: 30, borderRadius: '6px', border: `1.5px solid ${qrLoadingId === item.return_id ? '#d0d0d0' : '#0288d1'}`, color: qrLoadingId === item.return_id ? '#d0d0d0' : '#0288d1' }}>
                                        {qrLoadingId === item.return_id ? <CircularProgress size={13} /> : <QrCode2 sx={{ fontSize: 15 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  
                                  <Tooltip
                                    title={
                                      !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                        ? `Generate Order Invoice · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                        : 'Generate Order Invoice'
                                    }
                                    arrow
                                  >
                                    <span>
                                      <IconButton size="small" onClick={() => handleGenerateOrderInvoice(item)} disabled={invoiceLoadingId === item.return_id}
                                        sx={{ width: 30, height: 30, borderRadius: '6px', border: `1.5px solid ${invoiceLoadingId === item.return_id ? '#d0d0d0' : '#f57c00'}`, color: invoiceLoadingId === item.return_id ? '#d0d0d0' : '#f57c00' }}>
                                        {invoiceLoadingId === item.return_id ? <CircularProgress size={13} /> : <Receipt sx={{ fontSize: 15 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip
                                    title={
                                      !SKIP_AUTO_UPDATE_STATUSES.includes(item.status)
                                        ? `Generate Return Invoice · auto-set "${getStatusText(item.request_type?.toLowerCase() === 'refund' ? REFUND_AUTO_STATUS : REPLACEMENT_AUTO_STATUS)}"`
                                        : 'Generate Return Invoice'
                                    }
                                    arrow
                                  >
                                    <span>
                                      <IconButton size="small" onClick={() => handleGenerateReturnInvoice(item)} disabled={returnInvoiceLoadingId === item.return_id}
                                        sx={{ width: 30, height: 30, borderRadius: '6px', border: `1.5px solid ${returnInvoiceLoadingId === item.return_id ? '#d0d0d0' : '#9c27b0'}`, color: returnInvoiceLoadingId === item.return_id ? '#d0d0d0' : '#9c27b0' }}>
                                        {returnInvoiceLoadingId === item.return_id ? <CircularProgress size={13} /> : <ReceiptLong sx={{ fontSize: 15 }} />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  
                                  <QuickStatusActions item={item} onUpdate={handleQuickStatusUpdate} loadingId={quickActionLoadingId} />
                                </Box>
                              </Box>
                            </Card>
                          ))}
                        </Box>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Tab 1: Return Settings */}
                {tabValue === 1 && (
                  <Box sx={{ p: 3 }}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e1e3e5', boxShadow: 'none' }}>
                      <Box sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <AssignmentReturn sx={{ color: '#008060', fontSize: 24 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
                            Product Return Limit
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          Set the maximum number of days within which customers can return products after purchase.
                        </Typography>

                        {limitData && limitData.success && (
                          <Box sx={{ bgcolor: '#f6f6f7', p: 2, borderRadius: '8px', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Info sx={{ color: '#2196f3', fontSize: 20 }} />
                            <Typography variant="body2" color="text.secondary">
                              Current return limit: <strong>{returnLimit ? `${returnLimit} days` : 'Not set'}</strong>
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ maxWidth: 250 }}>
                          <TextField
                            label="Return Limit (Days)"
                            value={returnLimit}
                            onChange={handleReturnLimitChange}
                            fullWidth
                            type="text"
                            placeholder="e.g., 7"
                            helperText="Enter the number of days customers have to return products"
                            disabled={isLimitLoading}
                            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                          <Button
                            onClick={handleResetReturnLimit}
                            disabled={!hasReturnLimitChanges || isLimitSaving}
                            sx={{ textTransform: 'none', fontWeight: 600, color: '#202223', borderRadius: '8px', px: 2.5 }}
                          >
                            Reset
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={isLimitSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                            onClick={handleSaveReturnLimit}
                            disabled={!hasReturnLimitChanges || isLimitSaving || !returnLimit}
                            sx={{
                              bgcolor: '#008060', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5,
                              '&:hover': { bgcolor: '#006e52' },
                              '&.Mui-disabled': { bgcolor: '#c9cccf', color: '#8c9196' }
                            }}
                          >
                            {isLimitSaving ? 'Saving...' : 'Save Changes'}
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Details Dialog ── */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2.5,
            overflow: 'hidden',
            maxHeight: isMobile ? '100%' : '92vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.75,
            borderBottom: '1px solid #e5e7eb',
            bgcolor: '#fff',
            position: 'relative',
          }}
        >
          <Box sx={{ pr: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#172033', lineHeight: 1.2 }}>
              Return Details - #{selectedReturn?.return_id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customer return request and resolution information
            </Typography>
          </Box>

          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            aria-label="Close return details"
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              width: 36,
              height: 36,
              border: '1px solid #e5e7eb',
              bgcolor: '#fff',
              '&:hover': { bgcolor: '#f5f6f8' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: { xs: 1.5, sm: 2.5, md: 3 },
            bgcolor: '#f6f8fb',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 7 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#c7ccd5',
              borderRadius: 10,
            },
          }}
        >
          {selectedReturn && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Status */}
              <Card
                elevation={0}
                sx={{
                  border: '1px solid #e1e6ee',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: { xs: 1.75, sm: 2.5 },
                    py: 1.5,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#172033' }}>
                      Return Status
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current workflow status
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {getReturnStatusChip(selectedReturn)}
                    <Chip
                      size="small"
                      label={selectedReturn.return_approved ? 'Back-office approved' : 'Awaiting approval'}
                      color={selectedReturn.return_approved ? 'success' : 'warning'}
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ p: { xs: 1.75, sm: 2.5 }, bgcolor: '#fbfcfe' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                      gap: 1.5,
                      mb: !selectedReturn.return_approved ? 2 : 0,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Status Code</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                        {selectedReturn.status}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Return ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                        #{selectedReturn.return_id}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                        {formatDateTime(selectedReturn.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  {!selectedReturn.return_approved && (
                    <Box
                      sx={{
                        p: { xs: 1.25, sm: 1.5 },
                        borderRadius: 1.5,
                        border: '1px dashed #f0a44b',
                        bgcolor: '#fffaf3',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800, mb: 1 }}>
                        Select resolution before agent dispatch
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                          gap: 1,
                        }}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={resolutionLoading}
                          onClick={() => {
                            setResolutionChoice('replacement');
                            setResolutionDialogOpen(true);
                          }}
                          sx={{ minHeight: 42, fontWeight: 800, textTransform: 'none' }}
                        >
                          Replacement
                        </Button>

                        <Button
                          fullWidth
                          variant="contained"
                          color="warning"
                          disabled={resolutionLoading}
                          onClick={() => {
                            setResolutionChoice('wallet');
                            setResolutionDialogOpen(true);
                          }}
                          sx={{ minHeight: 42, fontWeight: 800, textTransform: 'none' }}
                        >
                          Voto Credits
                        </Button>

                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          disabled={resolutionLoading}
                          onClick={() => {
                            setResolutionChoice('refund');
                            setResolutionDialogOpen(true);
                          }}
                          sx={{ minHeight: 42, fontWeight: 800, textTransform: 'none' }}
                        >
                          Original Payment Refund
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Card>

              {/* Customer + Payment */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #dbe7f5',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.35, bgcolor: '#eef6ff', borderBottom: '1px solid #dbe7f5' }}>
                    <Typography sx={{ fontWeight: 800, color: '#1565c0' }}>
                      Customer Details
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {selectedReturn.customer_name || selectedReturn.guest_customer_name || '—'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Mobile Number</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {selectedReturn.customer_mobile || selectedReturn.guest_customer_mobile || '—'}
                        </Typography>
                      </Box>

                      <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                        <Typography variant="caption" color="text.secondary">Email Address</Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, mt: 0.25, overflowWrap: 'anywhere' }}
                        >
                          {selectedReturn.user_email || selectedReturn.guest_email || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid #dfe2f7',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.35, bgcolor: '#f3f1ff', borderBottom: '1px solid #dfe2f7' }}>
                    <Typography sx={{ fontWeight: 800, color: '#5e35b1' }}>
                      Payment Details
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Refund Mode</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {selectedReturn.refund_mode || '—'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Payment Status</Typography>
                        <Chip
                          size="small"
                          label={selectedReturn.return_approved ? 'Approved' : 'Pending Approval'}
                          color={selectedReturn.return_approved ? 'success' : 'warning'}
                          sx={{ mt: 0.4, fontWeight: 700 }}
                        />
                      </Box>

                      <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                        <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            mt: 0.25,
                            overflowWrap: 'anywhere',
                            fontFamily: 'monospace',
                          }}
                        >
                          {selectedReturn.transaction_id || '—'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>

              {/* Order + Financial Summary */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Card elevation={0} sx={{ border: '1px solid #e7dfd3', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.35, bgcolor: '#fff7ed', borderBottom: '1px solid #e7dfd3' }}>
                    <Typography sx={{ fontWeight: 800, color: '#e65100' }}>
                      Order Information
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Return ID</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          #{selectedReturn.return_id}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Order ID</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          #{selectedReturn.order_id}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Order Type</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {selectedReturn.order_type || '—'}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Created Date</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                          {formatDateTime(selectedReturn.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>

                <Card elevation={0} sx={{ border: '1px solid #e3ddf3', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.35, bgcolor: '#f8f5ff', borderBottom: '1px solid #e3ddf3' }}>
                    <Typography sx={{ fontWeight: 800, color: '#6a1b9a' }}>
                      Financial Summary
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                        gap: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">Unit Price</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.25 }}>
                          {formatCurrency(selectedReturn.price)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Quantity</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.25 }}>
                          {selectedReturn.return_qty || 0}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.25, color: '#6a1b9a' }}>
                          {formatCurrency(selectedReturn.total)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Box>

              {/* Request Details */}
              <Card elevation={0} sx={{ border: '1px solid #ead9e5', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.35, bgcolor: '#fff5fa', borderBottom: '1px solid #ead9e5' }}>
                  <Typography sx={{ fontWeight: 800, color: '#c2185b' }}>
                    Return Request Details
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">Request Type</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedReturn.request_type?.toLowerCase() === 'refund' ? 'Refund' : 'Replacement'}
                          size="small"
                          color={selectedReturn.request_type?.toLowerCase() === 'refund' ? 'primary' : 'secondary'}
                          sx={{ fontWeight: 700 }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ gridColumn: { xs: 'auto', sm: 'span 2' } }}>
                      <Typography variant="caption" color="text.secondary">Product</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                        {selectedReturn.product_name || `Product ID ${selectedReturn.product_id}`}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary">Quantity</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
                        {selectedReturn.return_qty || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>

              {/* Product + Damage Photos */}
              <Card elevation={0} sx={{ border: '1px solid #dbe4ef', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.35, bgcolor: '#f1f7fd', borderBottom: '1px solid #dbe4ef' }}>
                  <Typography sx={{ fontWeight: 800, color: '#1976d2' }}>
                    Product Reference & Issue Evidence
                  </Typography>
                </Box>

                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  {(() => {
                    let photos: string[] = [];
                    const raw: any = selectedReturn.damage_photos;

                    try {
                      if (Array.isArray(raw)) {
                        photos = raw
                          .map((p: any) => typeof p === 'string' ? p : (p?.url || p?.path || p?.image || ''))
                          .filter(Boolean);
                      } else if (typeof raw === 'string') {
                        const value = raw.trim();

                        if (value) {
                          try {
                            const parsed = JSON.parse(value);
                            photos = Array.isArray(parsed)
                              ? parsed
                                .map((p: any) => typeof p === 'string' ? p : (p?.url || p?.path || p?.image || ''))
                                .filter(Boolean)
                              : value.split(',').map((p: string) => p.trim()).filter(Boolean);
                          } catch {
                            photos = value.split(',').map((p: string) => p.trim()).filter(Boolean);
                          }
                        }
                      } else if (raw && typeof raw === 'object') {
                        photos = Object.values(raw)
                          .map((p: any) => typeof p === 'string' ? p : (p?.url || p?.path || p?.image || ''))
                          .filter(Boolean);
                      }
                    } catch (error) {
                      console.warn('Unable to parse return damage photos:', error);
                    }

                    const photoItems = [
                      ...(selectedReturn.product_image
                        ? [{
                            key: `product-${selectedReturn.product_image}`,
                            label: 'Product Reference',
                            url: getReturnImageUrl(selectedReturn.product_image, 'product'),
                          }]
                        : []),
                      ...photos.map((photo, idx) => ({
                        key: `damage-${photo}-${idx}`,
                        label: `Issue Evidence ${idx + 1}`,
                        url: getReturnImageUrl(photo, 'damage'),
                      })),
                    ];

                    return photoItems.length ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            sm: 'repeat(4, minmax(0, 1fr))',
                            md: 'repeat(5, minmax(0, 1fr))',
                          },
                          gap: 1.5,
                        }}
                      >
                        {photoItems.map((photo) => (
                          <Box key={photo.key} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: 'block',
                                mb: 0.75,
                                fontWeight: 700,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {photo.label}
                            </Typography>

                            <Box
                              onClick={() => setImagePreview({ url: photo.url, title: photo.label })}
                              sx={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                border: '1px solid #d9dee7',
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                bgcolor: '#f7f8fa',
                                cursor: 'zoom-in',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all .15s ease',
                                '&:hover': {
                                  boxShadow: 2,
                                  borderColor: '#1976d2',
                                  transform: 'translateY(-1px)',
                                },
                              }}
                            >
                              <img
                                src={photo.url}
                                alt={photo.label}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          py: 3,
                          textAlign: 'center',
                          border: '1px dashed #cfd5df',
                          borderRadius: 1.5,
                          bgcolor: '#fafbfc',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No product or damage photo uploaded.
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Card>

              {/* Reason + Notes */}
              <Card elevation={0} sx={{ border: '1px solid #e4e7ec', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.35, bgcolor: '#f9fafb', borderBottom: '1px solid #e4e7ec' }}>
                  <Typography sx={{ fontWeight: 800, color: '#374151' }}>
                    Reason & Notes
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Return Reason
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.75,
                        p: 1.5,
                        minHeight: 58,
                        bgcolor: '#f5f6f8',
                        borderRadius: 1.5,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <Typography variant="body2">
                        {selectedReturn.reason || 'No reason provided.'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      Customer Notes
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.75,
                        p: 1.5,
                        minHeight: 58,
                        bgcolor: '#f5f6f8',
                        borderRadius: 1.5,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <Typography variant="body2">
                        {selectedReturn.notes || 'No additional notes.'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>

            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Image preview dialog */}
      <Dialog
        open={Boolean(imagePreview)}
        onClose={() => setImagePreview(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {imagePreview?.title || 'Image Preview'}
          <IconButton
            onClick={() => setImagePreview(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f5f5f5',
            p: 2,
          }}
        >
          {imagePreview?.url && (
            <img
              src={imagePreview.url}
              alt={imagePreview.title}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                display: 'block',
                borderRadius: 8,
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Resolution confirmation dialog */}
      <Dialog open={resolutionDialogOpen} onClose={() => !resolutionLoading && setResolutionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Return Resolution</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This approval allows the return to move into processing and agent dispatch.
          </Typography>
          <Typography variant="subtitle2">
            Selected: {resolutionChoice === 'replacement' ? 'Replacement' : resolutionChoice === 'wallet' ? 'Voto Credits' : 'Original Payment Refund'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolutionDialogOpen(false)} disabled={resolutionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => resolutionChoice && handleApproveReturn(resolutionChoice)}
            disabled={!resolutionChoice || resolutionLoading}
            startIcon={resolutionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          >
            {resolutionLoading ? 'Approving...' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Dialog */}
      <Dialog open={qrDialogOpen} onClose={handleCloseQrDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          QR Code – Return #{qrForReturn?.return_id}
          <IconButton onClick={handleCloseQrDialog} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {qrForReturn && (
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ mb: 2, p: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography>Order #{qrForReturn.order_id}</Typography>
                <Typography variant="body2">{qrForReturn.product_name}</Typography>
                <Chip size="small" label={qrForReturn.request_type} />
              </Box>
              {qrImageUrl ? (
                <img src={qrImageUrl} alt="QR" style={{ maxWidth: 200, border: '1px solid #eee', borderRadius: 8 }} />
              ) : (
                <Box sx={{ p: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading QR image...
                  </Typography>
                </Box>
              )}
              {generatedQR?.pickupCode && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption">Pickup Code</Typography>
                  <Typography variant="h5" sx={{ fontFamily: 'monospace', letterSpacing: 4, p: 1, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    {generatedQR.pickupCode}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadQR} startIcon={<Download />} disabled={!qrImageUrl}>Download</Button>
          <Button onClick={handlePrintQR} startIcon={<Print />} variant="contained" disabled={!generatedQR}>Print</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' },
            '& .MuiAlert-message': { color: 'white' },
            '& .MuiAlert-action': { color: 'white' },
            '& .MuiSvgIcon-root': { color: 'white' },
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReturnProcessManagement;